import { IconComponent, Spinner, useErrorHandler } from "@@common";
import CloseIcon from "@mui/icons-material/Close";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import useViewOnOpen from "../../Hooks/useViewOnOpen";
import { DialogActionModel } from "../../Models/DialogActionModel";
import { ViewModel } from "../../Models/ViewModel";
import ComponentFactory from "../ComponentFactory";
import getComponentLayout from "../Table/getComponentLayout";
import useViewExpressionEvaluatorContext from "./useViewExpressionEvaluatorContext";

type ViewBodyComponentProps = {
  isPending: boolean;
  children: any[];
  component: ViewModel;
  context: any;
  sx?: any;
};

const ViewBodyComponent: React.FC<ViewBodyComponentProps> = ({ isPending, children, component, context, sx }) => {
  return (
    <Stack direction="column" gap={2} sx={sx}>
      {isPending && <Spinner />}
      {!isPending && children.map((child) => <ComponentFactory key={child.Id} parent={component} component={child} context={context} />)}
    </Stack>
  );
};

type ViewComponentProps = {
  component: ViewModel;
  open?: boolean;
  input?: Record<string, any>;
  setOutput?(output: Record<string, any> | undefined): void;
};

const ViewComponent: React.FC<ViewComponentProps> = ({ component, open, input, setOutput }) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  // for the Dialog views the input comes from the props, for non-Dialog views from the URL params (path + query string)
  const inputObj = useMemo(
    () => (component.Dialog ? input : { ...Object.fromEntries(searchParams.entries()), ...params }),
    [component.Dialog, input, params, searchParams]
  );
  const viewContext = useViewExpressionEvaluatorContext(component, inputObj);

  const [onOpenResult, onOpenPending] = useViewOnOpen(component, viewContext, "ViewComponent");

  const [actionPending, setActionPending] = useState<number | null>(null);

  const errorHandler = useErrorHandler();
  const handleAction = async (actionKey: number, action: DialogActionModel) => {
    if (!action.OnSelected) {
      setOutput?.(undefined);
      return;
    }

    setActionPending(actionKey);
    try {
      var result = await viewContext.Evaluator.evaluate(action.OnSelected, viewContext);
      setOutput?.(result);
    } catch (error) {
      errorHandler(error);
    } finally {
      setActionPending(null);
    }
  };

  const isPending = onOpenPending || (!!component.OnOpen && !onOpenResult);

  const componentLayoutSx = getComponentLayout(component);

  if (component.Dialog) {
    return (
      <Dialog open={open ?? true} onClose={() => setOutput?.(undefined)} maxWidth="md" fullWidth>
        {component.Title && (
          <DialogTitle>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" gap={1}>
                {component.Dialog.IconName && <IconComponent iconName={component.Dialog.IconName} />}
                {component.Title}
              </Stack>
              <IconButton onClick={() => setOutput?.(undefined)} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
        )}
        <DialogContent>
          <ViewBodyComponent
            isPending={isPending}
            component={component}
            children={component.Children}
            context={viewContext}
            sx={{ ...componentLayoutSx, pt: 2 }}
          />
        </DialogContent>
        {component.Dialog.Actions && component.Dialog.Actions.length > 0 && (
          <DialogActions>
            {component.Dialog.Actions.map((action, index) => (
              <Button
                key={index}
                startIcon={action.IconName ? <IconComponent iconName={action.IconName} /> : undefined}
                loading={actionPending === index}
                disabled={actionPending !== null}
                onClick={() => handleAction(index, action)}
              >
                {action.Label}
              </Button>
            ))}
          </DialogActions>
        )}
      </Dialog>
    );
  }

  return <ViewBodyComponent isPending={isPending} component={component} children={component.Children} context={viewContext} sx={componentLayoutSx} />;
};

export default ViewComponent;
